import type { JSX } from 'react'
import type { FIXED_INDENTS } from 'vars/fixed-indents'
import type { ICard } from '../../Card'
import type { CardTypeT } from '../../Card.h'

import { IconNames, IconSizes } from 'atoms/Icon'
import { TextBox, TextBoxTypes } from 'atoms/TextBox'
import { Colors, TextAligning } from 'atoms/TextBox/TextBox.h'
import { useTvCardBackroundCover } from 'blocks/Cards/TVCard/lib'
import { useBooleanState } from 'utils/hooks'
import { COLOR } from 'vars/color'
import { CardAnimations, CardCorners, CardType } from '../../Card.h'
import { getCardASpectRatio, getTransformScale } from './lib'
import {
  CardContent,
  TitleComponentSC,
  VodSimpleCardSC,
} from './MovieCardTemplate.styled'
import { VodSimpleCardContainerSC } from './TopVodSimpleCard.styled'

export interface IVodSimpleCard extends Omit<ICard, 'children'> {
  type: CardTypeT
  contentPadding?: FIXED_INDENTS
  title?: string
  covers?: Record<string, any>
  titleFont?: TextBoxTypes
  verticalVideo?: boolean
}

export const VodSimpleCard = ({
  posterUrl,
  width = '100%',
  childrenTransition = CardAnimations.FadeIn,
  viewedButtonFavorite = true,
  lockedSizeIcon = IconSizes.xxxLarge,
  backgroundColor = COLOR.SURFACE_DARK_4,
  lockedColor = COLOR.TEXT_ICON_PRIMARY,
  type = CardType.HORIZONTAL,
  contentPadding,
  hidePriceOnHover = false,
  overlayEnabled = false,
  overlayOnHoverEnabled = false,
  locked,
  title = '',
  covers,
  titleFont = TextBoxTypes.Footnote5,
  lockedIcon = IconNames.tvPinLock,
  detailsCard = false,
  corners = detailsCard ? CardCorners.CORNERS_0 : CardCorners.CORNERS_10,
  ...rest
}: IVodSimpleCard): JSX.Element => {
  const [isPosterLoaded, handlePosterLoading, handlePosterError] =
    useBooleanState(true)

  const cardAspectRatio = getCardASpectRatio(type)
  const transformScale = getTransformScale(type)

  const poster = isPosterLoaded ? posterUrl : ''
  const emptyPoster = !poster || !isPosterLoaded

  const BackgroundCover = locked ? (
    useTvCardBackroundCover(title, covers)
  ) : (
    <></>
  )

  const posterContent = (
    <TitleComponentSC>
      {emptyPoster || locked ? (
        <TextBox
          font={titleFont}
          maxLines={2}
          title={title}
          color={Colors.TEXT_ICON_PRIMARY}
          alignment={TextAligning.Center}
        />
      ) : (
        <></>
      )}
    </TitleComponentSC>
  )

  return (
    <VodSimpleCardContainerSC>
      <VodSimpleCardSC
        posterUrl={poster}
        width={width}
        childrenTransition={childrenTransition}
        aspectRatio={cardAspectRatio}
        backgroundColor={backgroundColor}
        lockedColor={lockedColor}
        lockedSizeIcon={lockedSizeIcon}
        hidePriceOnHover={hidePriceOnHover}
        overlayEnabled={overlayEnabled}
        overlayOnHoverEnabled={overlayOnHoverEnabled}
        transformScale={transformScale}
        locked={locked}
        backgroundCover={BackgroundCover}
        posterContent={posterContent}
        lockedIcon={lockedIcon}
        viewedButtonFavorite={false}
        handlePosterLoading={handlePosterLoading}
        handlePosterError={handlePosterError}
        corners={corners}
        detailsCard={detailsCard}
        {...rest}
      >
        <CardContent
          type={type}
          viewedButtonFavorite={viewedButtonFavorite}
          padding={contentPadding}
        ></CardContent>
      </VodSimpleCardSC>
    </VodSimpleCardContainerSC>
  )
}
